import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { resolvePermissionCheck } from '@/utils/groupPermissions';
import { getApiWriteEpoch } from '@/utils/apiWriteEpoch';
import { withSelectedIds } from '@/utils/selectionStats';

const {
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

const DEFAULT_USER_FILTERS = { offset: 0, limit: 20, ordering: '-order_id' };

let instanceCounter = 0;

/**
 * In-flight list request per store module instance. Concurrent dispatches asking
 * for the very same page used to cancel each other: the server still paid for
 * the canceled query. They now share a single request.
 * @type {Map<number, { key: string, epoch: number, promise: Promise<unknown> }>}
 */
const inflightFetches = new Map();

/**
 * @param {string} url
 * @param {Record<string, unknown>} filters
 * @returns {string}
 */
const requestKey = (url, filters) => {
    const params = filters || {};
    const serialized = Object.keys(params)
        .sort()
        .map((field) => `${field}=${String(params[field])}`)
        .join('&');
    return `${url}?${serialized}`;
};

const hasSectionOverlay = (sectionFilters) =>
    sectionFilters && Object.keys(sectionFilters).length > 0;

const isSectionOverlayMode = (state) =>
    !!(state._storageOptions?.sectionOverlay || hasSectionOverlay(state.orders_section_filters));

const persistUserFilters = (state) => {
    const { storageKey, fieldsToSave, sanitizeUserFilters } = state._storageOptions || {};
    if (!storageKey) {
        return;
    }
    let filters = isSectionOverlayMode(state)
        ? state.orders_user_filters
        : state.orders_filters;
    if (typeof sanitizeUserFilters === 'function') {
        filters = sanitizeUserFilters(filters);
        if (isSectionOverlayMode(state)) {
            state.orders_user_filters = filters;
        }
    }
    saveFiltersToStorage(storageKey, filters, fieldsToSave);
};

const applyOrdersFiltersOverlay = (state, { resetOffset = false, offset } = {}) => {
    const previousOffset = state.orders_filters?.offset ?? 0;
    if (hasSectionOverlay(state.orders_section_filters)) {
        state.orders_filters = mergeUserAndSectionFilters(
            state.orders_user_filters,
            state.orders_section_filters,
        );
    } else {
        state.orders_filters = {
            ...DEFAULT_USER_FILTERS,
            ...(state.orders_user_filters || {}),
        };
    }
    if (offset !== undefined) {
        state.orders_filters.offset = offset;
    } else {
        state.orders_filters.offset = resetOffset ? 0 : previousOffset;
    }
};

const createState = (options = {}) => ({
    orders: [],
    orders_url: '/api/customerorders/',
    orders_stats: {},
    orders_stats_loading: false,
    /** Effective filters for API / Table when no overlay; with overlay, API uses this. */
    orders_filters: { ...DEFAULT_USER_FILTERS },
    /** User-chosen filters persisted across section overlays. */
    orders_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Hard constraints from the parent (tab/section); win over user prefs on those keys. */
    orders_section_filters: {},
    orders_user_filters_hydrated: false,
    orders_count: 0,
    orders_loaded: false,
    orders_loading: false,
    orders_loading_errors: null,
    orders_cancel_source: null,
    orders_cancel_source_stats: null,
    /** Rows checked in the parent listing (selection lives outside this submodule). */
    stats_selected_items: [],
    fetch_stats: true,
    _storageOptions: options || {},
    /** Scopes the in-flight request registry to this module instance. */
    _instanceId: ++instanceCounter,
});

const mutations = {
    updateOrders(state, orders) {
        state.orders = orders;
    },
    updateOrdersUrl(state, orders_url) {
        state.orders_url = orders_url;
    },
    updateOrdersFilters(state, orders_filters) {
        if (isSectionOverlayMode(state)) {
            state.orders_user_filters = extractUserFiltersFromEffective(
                orders_filters,
                state.orders_section_filters,
                state.orders_user_filters,
            );
            state.orders_user_filters_hydrated = true;
            persistUserFilters(state);
            applyOrdersFiltersOverlay(state, { offset: orders_filters?.offset ?? 0 });
            return;
        }
        state.orders_filters = orders_filters;
        state.orders_user_filters = { ...(orders_filters || {}) };
        persistUserFilters(state);
    },
    updateOrdersSectionFilters(state, section_filters) {
        state.orders_section_filters = section_filters || {};
    },
    hydrateOrdersUserFilters(state, user_filters) {
        state.orders_user_filters = {
            ...DEFAULT_USER_FILTERS,
            ...(user_filters || {}),
        };
        state.orders_user_filters_hydrated = true;
    },
    rebuildOrdersFilters(state, options = {}) {
        applyOrdersFiltersOverlay(state, options);
    },
    updateOrdersLoading(state, orders_loading) {
        state.orders_loading = orders_loading;
    },
    updateOrdersLoaded(state, orders_loaded) {
        state.orders_loaded = orders_loaded;
    },
    updateOrdersLoadingErrors(state, orders_loading_errors) {
        state.orders_loading_errors = orders_loading_errors;
    },
    updateOrdersCount(state, orders_count) {
        state.orders_count = orders_count;
    },
    updateOrdersStats(state, orders_stats) {
        state.orders_stats = orders_stats;
    },
    updateOrdersStatsLoading(state, loading) {
        state.orders_stats_loading = loading;
    },
    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
    updateStatsSelectedItems(state, items) {
        state.stats_selected_items = items || [];
    },
    updateOrdersCancelSource(state, source) {
        state.orders_cancel_source = source;
    },
    updateOrdersCancelSourceStats(state, source) {
        state.orders_cancel_source_stats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    patchOrder(state, order) {
        if (!order?.id) {
            return;
        }
        const index = state.orders.findIndex((o) => o.id === order.id);
        if (index === -1) {
            return;
        }
        state.orders.splice(index, 1, { ...state.orders[index], ...order });
    },

};

const canViewOrders = () =>
    resolvePermissionCheck(
        store.getters['session/current_user_permissions'],
        'order.view_order',
    );

const actions = {

    /**
     * @param {{ force?: boolean }} [params] force replays the request even when
     * an identical one is already in flight.
     */
    async fetchOrders({ commit, dispatch, state }, params) {
        if (!canViewOrders()) {
            commit('updateOrdersLoading', false);
            commit('updateOrdersLoaded', true);
            return;
        }

        const instanceId = state._instanceId;
        const key = requestKey(state.orders_url, state.orders_filters);
        const epoch = getApiWriteEpoch();
        const inflight = inflightFetches.get(instanceId);
        // A response started before the last write may be stale: only share the
        // in-flight request when nothing was written since it left.
        if (
            inflight
            && params?.force !== true
            && inflight.key === key
            && inflight.epoch === epoch
        ) {
            return inflight.promise;
        }

        commit('updateOrdersLoading', true);
        commit('updateOrdersLoadingErrors', null);
        if (state.fetch_stats) {
            // Avoid flashing zeros in TableSummaryPanel between list load and stats fetch.
            commit('updateOrdersStatsLoading', true);
        }

        if (state.orders_cancel_source) {
            state.orders_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateOrdersCancelSource', cancelSource);
        const requestUrl = state.orders_url;
        const request = (async () => {
            try {
                const response = await axios.get(requestUrl, {
                    params: state.orders_filters,
                    cancelToken: cancelSource.token,
                });
                if (state.orders_url !== requestUrl) {
                    return;
                }
                commit('updateOrdersLoading', false);
                commit('updateOrders', response.data.results);
                commit('updateOrdersCount', response.data.count);
                commit('updateOrdersCancelSource', null);
                commit('updateOrdersLoaded', true);
                dispatch('fetchOrdersStats');

                return response;
            } catch (xhr_error) {
                if (axios.isCancel(xhr_error)) {
                    return;
                }
                if (state.orders_url !== requestUrl) {
                    return;
                }
                const error = utils.handleError(xhr_error);
                commit('updateOrdersLoadingErrors', error.details);
                commit('updateOrdersLoading', false);
                commit('updateOrdersCancelSource', null);
                return Promise.reject(error);
            }
        })();

        inflightFetches.set(instanceId, { key, epoch, promise: request });
        try {
            return await request;
        } finally {
            if (inflightFetches.get(instanceId)?.promise === request) {
                inflightFetches.delete(instanceId);
            }
        }
    },

    async fetchOrdersStats({ commit, dispatch, state }, params) {
        if (!canViewOrders()) {
            commit('updateOrdersStatsLoading', false);
            return;
        }
        if (!state.fetch_stats) {
            commit('updateOrdersStatsLoading', false);
            return;
        }

        // Cancel any pending request
        if (state.orders_cancel_source_stats) {
            state.orders_cancel_source_stats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateOrdersCancelSourceStats', source);
        commit('updateOrdersStatsLoading', true);

        const requestOrdersUrl = state.orders_url;
        const statsUrl = `${requestOrdersUrl}stats/`;
        try {
            const response = await axios.get(statsUrl, {
                params: withSelectedIds(state.orders_filters, state.stats_selected_items),
                cancelToken: source.token,
            });
            if (state.orders_url !== requestOrdersUrl) {
                return;
            }
            commit('updateOrdersStats', response.data);
            commit('updateOrdersStatsLoading', false);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            commit('updateOrdersStatsLoading', false);
            throw utils.handleError(xhr_error);
        }
    },

    /**
     * Apply tab/section constraints without resetting the list module.
     * Hydrates shared user filters once, then rebuilds the effective overlay.
     */
    async applySectionFilters({ commit, state }, params = {}) {
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }

        // Persist in-memory user prefs before swapping the tab overlay so a
        // remount can reload them from the shared storage key.
        if (state.orders_user_filters_hydrated) {
            persistUserFilters(state);
        }

        const sectionFilters = params.filters || {};
        await commit('updateOrdersSectionFilters', sectionFilters);

        const storageKey = state._storageOptions?.storageKey;
        if (!state.orders_user_filters_hydrated) {
            const loaded = storageKey
                ? loadFiltersFromStorage(
                    storageKey,
                    { ...DEFAULT_USER_FILTERS },
                    state._storageOptions?.fieldsToExclude,
                )
                : { ...DEFAULT_USER_FILTERS };
            // Pass loaded as previousUser so constraint keys present in storage
            // (e.g. agent_commission_status set on "all") are kept for later tabs.
            let userFilters = extractUserFiltersFromEffective(
                loaded,
                sectionFilters,
                loaded,
            );
            if (typeof state._storageOptions?.sanitizeUserFilters === 'function') {
                userFilters = state._storageOptions.sanitizeUserFilters(userFilters);
            }
            await commit('hydrateOrdersUserFilters', userFilters);
            if (storageKey) {
                saveFiltersToStorage(
                    storageKey,
                    userFilters,
                    state._storageOptions?.fieldsToSave,
                );
            }
        }

        await commit('rebuildOrdersFilters', {
            resetOffset: params.resetOffset !== false,
        });
    },

    async init({ commit, dispatch, state }, params) {
        inflightFetches.delete(state._instanceId);
        if (state.orders_cancel_source) {
            state.orders_cancel_source.cancel("canceled");
            commit('updateOrdersCancelSource', null);
        }
        if (state.orders_cancel_source_stats) {
            state.orders_cancel_source_stats.cancel("canceled");
            commit('updateOrdersCancelSourceStats', null);
        }

        await commit('updateOrders', []);
        await commit('updateOrdersStats', {});
        await commit('updateStatsSelectedItems', []);
        await commit('updateFetchStats', params.fetch_stats === undefined ? true : params.fetch_stats);
        await commit('updateOrdersLoaded', false);
        
        if (params.url) {
            await commit('updateOrdersUrl', params.url);
        }

        const useOverlay = !!(
            params.sectionOverlay
            || params.storageOptions?.sectionOverlay
            || state._storageOptions?.sectionOverlay
        );

        if (useOverlay) {
            await dispatch('applySectionFilters', {
                filters: params.filters || {},
                storageOptions: params.storageOptions,
                resetOffset: true,
            });
            return;
        }

        // Legacy path: merge defaults + filters + storage into effective filters.
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        const storageKey = state._storageOptions?.storageKey;
        await commit('updateOrdersSectionFilters', {});
        await commit('updateOrdersFilters', {
            ...DEFAULT_USER_FILTERS,
            ...(params.filters || {}),
            ...(storageKey
                ? loadFiltersFromStorage(
                    storageKey,
                    {},
                    state._storageOptions?.fieldsToExclude,
                )
                : {}),
        });
    },

    async updateAgentCommissionStatus({ commit, dispatch, state }, { order, status }) {
        if (store.getters['session/current_user_permissions'].indexOf("order.change_order") == -1) {
            return;
        }
        try {
            const response = await axios.post(`/api/customerorders/${order}/agent_commission_status/`, { status })
            dispatch('fetchOrders');
            dispatch('session/fetchStats', null, { root: true });
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateOrdersLoadingErrors', error.details);
            throw error;
        }
    },

    async billAgentCommission({ commit, dispatch, state }, { order }) {
        if (store.getters['session/current_user_permissions'].indexOf("order.change_order") == -1) {
            return;
        }
        try {
            const response = await axios.post(`/api/customerorders/${order}/bill_agent_commission/`);
            dispatch('fetchOrders');
            dispatch('session/fetchStats', null, { root: true });
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateOrdersLoadingErrors', error.details);
            throw error;
        }
    }
};

const getters = {
    /**
     * Filters bound to Table UI: section constraints are omitted so tabs don't
     * surface forced values as user chips. API still uses orders_filters.
     */
    orders_table_filters(state) {
        if (!isSectionOverlayMode(state) || !hasSectionOverlay(state.orders_section_filters)) {
            return state.orders_filters;
        }
        return omitFilterKeys(
            state.orders_filters,
            getSectionConstraintKeys(state.orders_section_filters),
        );
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
    getters,
});
